import React from 'react';
import { withTranslation } from 'react-i18next';
import { netLibConf } from 'consts';
import NotAuthorizedError from 'errors/notAuthorizedError';
import { EnelXGuiAtoms, EnelXGuiComponents } from 'gui';
import i18n from 'i18n';
import EnelXComLib from 'net-com-lib';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { SpanError } from 'utils';

import { ErrorCentralText, ErrorSmallText, ErrorWrapper } from './style';

const { MigrationButton, MigrationImage } = EnelXGuiAtoms;
const { MigrationLoader } = EnelXGuiComponents;

const NetLib = EnelXComLib(netLibConf);

const isProduction = process.env.ENVIRONMENT === 'prod';
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      title: null,
      subtitle: null,
      icon: 'sync',
      error: null,
      errorInfo: null,
      isLoading: false,
      logoutButton: false,
      logoutError: false,
      closeTabButton: false,
      buttonText: '',
    };

    this.closeTab = () => {
      window.close();
    };

    this.reload = () => window.location.reload();

    this.logOut = async () => {
      const stateCopy = { ...this.state };
      stateCopy.isLoading = true;
      stateCopy.logoutError = false;
      this.setState(stateCopy);
      try {
        await NetLib.LoginUtils.doLogout();
      } catch {
        stateCopy.logoutError = true;
      } finally {
        stateCopy.isLoading = false;
        this.setState(stateCopy);
      }
    };

    this.handleClick = () => {
      if (this.state.logoutButton) return this.logOut();
      if (this.state.closeTabButton) return this.closeTab();
      return this.reload();
    };

    i18n.on('failedLoading', (lng, _ns, error) => {
      if (lng === 'en') {
        const stateCopy = { ...this.state };
        stateCopy.error = error;
        stateCopy.title = 'Unable to load translations';
        stateCopy.buttonText = 'Refresh the page';
        this.setState(stateCopy);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`Unable to locate ${lng} language. Using english fallback`);
      }
    });
  }

  componentDidCatch(error, errorInfo) {
    const newState = {
      ...this.state,
      error,
      errorInfo,
      subtitle: '',
      buttonText: this.props.t('errors.refreshPage'),
    };

    if (error instanceof NotAuthorizedError) {
      newState.subtitle = this.props.t('errors.notAuthorizedContactCenter');
      newState.logoutButton = true;
      newState.icon = 'logout';
      newState.buttonText = this.props.t('navigation.logout');
    } else {
      newState.title = this.props.t('errors.wentWrong');
      newState.subtitle = this.props.t('errors.plsRefresh');
    }

    if (window[process.env.DEBUG_KEY] && isProduction) {
      window[process.env.DEBUG_KEY].boundaryError = {
        error,
        errorInfo,
      };
    }

    this.setState(newState);
  }

  render() {
    if (this.state.error) {
      return (
        <>
          {this.state.isLoading ? <MigrationLoader /> : null}
          <ErrorWrapper>
            <MigrationImage source={this.props.theme?.config?.errorPageLogo} />
            <ErrorCentralText>{this.state.title}</ErrorCentralText>
            <ErrorSmallText>{this.state.subtitle}</ErrorSmallText>
            {this.state.logoutError
              ? <SpanError>{this.props.t('errors.retryInAWhile')}</SpanError>
              : null}
            { (!isProduction && this.state.error)
              ? (
                <>
                  <ErrorSmallText>{this.state.error.toString()}</ErrorSmallText>
                  <ErrorSmallText>{this.state.errorInfo?.componentStack}</ErrorSmallText>
                </>
              )
              : null}
            <MigrationButton
              btnText={this.state.buttonText}
              btnType="primary"
              iconLeft
              iconLibrary="material"
              iconName={this.state.icon}
              onClick={this.handleClick}
            />
          </ErrorWrapper>
        </>
      );
    }

    return this.props.children ?? null;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  theme: PropTypes.shape({
    config: PropTypes.shape({
      errorPageLogo: PropTypes.string.isRequired,
      userNotAuthorizedLogo: PropTypes.string.isRequired,
    }).isRequired,
    colors: PropTypes.shape({
      accent: PropTypes.shape({
        primary: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withTheme(ErrorBoundary));
